import { useEffect, useState } from "react";
import { CheckCircle2, Clock3, HelpCircle, RefreshCw, XCircle } from "lucide-react";

/**
 * Indicador compacto de janela da API Oficial.
 * Importante: falta de evento oficial NAO significa janela encerrada.
 * Este componente nao altera permissão de envio nem usa eventos da Z-API.
 */
const pad = (n) => String(n).padStart(2, "0");

function tempoRestante(ms) {
  const minutos = Math.max(0, Math.ceil(ms / 60000));
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return horas ? `${horas}h${pad(resto)}m` : `${minutos}min`;
}

function horaBrasilia(iso) {
  if (!iso || !Number.isFinite(Date.parse(iso))) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

export function classificarJanelaVisual(dados, agora = Date.now()) {
  if (!dados) return { tipo: "CONSULTANDO", nome: "Verificando janela", restanteMs: 0 };
  if (dados.estado === "DATA_INVALIDA" || !dados.confirmada_por_meta || !dados.janela_expira_em) {
    return { tipo: "INCERTA", nome: "Janela não confirmada", restanteMs: 0 };
  }
  const expira = Date.parse(dados.janela_expira_em);
  if (!Number.isFinite(expira)) return { tipo: "INCERTA", nome: "Janela não confirmada", restanteMs: 0 };
  const restanteMs = Math.max(0, expira - agora);
  if (!restanteMs) return { tipo: "ENCERRADA", nome: "Janela fechada", restanteMs: 0 };
  if (restanteMs <= 30 * 60000) return { tipo: "URGENTE", nome: "Janela aberta", restanteMs };
  if (restanteMs <= 2 * 3600000) return { tipo: "ATENCAO", nome: "Janela aberta", restanteMs };
  return { tipo: "ABERTA", nome: "Janela aberta", restanteMs };
}

export default function MetaWindowStatusView({
  data,
  compact = false,
  onRefresh = null,
  refreshing = false,
  error = "",
}) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setAgora(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);

  const situacao = error && !data
    ? { tipo: "INDISPONIVEL", nome: "Janela indisponível", restanteMs: 0 }
    : classificarJanelaVisual(data, agora);
  const aberta = ["ABERTA", "ATENCAO", "URGENTE"].includes(situacao.tipo);
  const alerta = ["ATENCAO", "URGENTE"].includes(situacao.tipo);
  const className = aberta
    ? alerta
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-emerald-200 bg-emerald-50 text-emerald-800"
    : situacao.tipo === "ENCERRADA"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-slate-200 bg-slate-50 text-slate-600";
  const Icon = aberta ? CheckCircle2 : situacao.tipo === "ENCERRADA" ? XCircle : situacao.tipo === "CONSULTANDO" ? Clock3 : HelpCircle;
  const detalhes = data?.confirmada_por_meta && data?.janela_expira_em
    ? `Última entrada oficial: ${horaBrasilia(data.ultima_entrada)}. Fechamento: ${horaBrasilia(data.janela_expira_em)}.`
    : "Sem evento oficial suficiente para confirmar a janela deste contato.";
  const dica = error ? `${detalhes} Não foi possível atualizar: ${error}` : detalhes;

  return (
    <span
      role="status"
      aria-live="polite"
      data-meta-window-status={situacao.tipo}
      title={dica}
      className={`inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold leading-4 ${className}`}
    >
      <Icon size={12} className="shrink-0" />
      <span className="whitespace-nowrap">{situacao.nome}</span>
      {aberta && situacao.restanteMs > 0 && (
        <span className="whitespace-nowrap border-l border-current/25 pl-1.5 tabular-nums" aria-label={`Tempo restante: ${tempoRestante(situacao.restanteMs)}`}>
          {tempoRestante(situacao.restanteMs)}
        </span>
      )}
      {!compact && typeof onRefresh === "function" && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          title="Atualizar janela oficial"
          aria-label="Atualizar janela oficial"
          className="ml-0.5 rounded p-0.5 opacity-70 hover:opacity-100 disabled:opacity-40"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
        </button>
      )}
    </span>
  );
}
